import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const GOOGLE_SCRIPT_ID = 'google-identity-services';

function loadGoogleScript() {
  if (document.getElementById(GOOGLE_SCRIPT_ID)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function GoogleSignInButton({ mode = 'login', text = 'signin_with' }) {
  const nav = useNavigate();
  const buttonRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!clientId) return;
    let active = true;

    loadGoogleScript()
      .then(() => {
        if (!active || !window.google?.accounts?.id || !buttonRef.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async ({ credential }) => {
            if (!credential) {
              toast.error('Google sign-in failed');
              return;
            }
            setLoading(true);
            try {
              const { data } = await api.post('/auth/google', { credential, mode });
              localStorage.setItem('hb_token', data.token);
              if (data.refresh_token) localStorage.setItem('hb_refresh_token', data.refresh_token);
              localStorage.setItem('hb_user', JSON.stringify(data.user));
              localStorage.removeItem('hb_doctor');
              toast.success(`Welcome, ${data.user.name}`);
              nav('/dashboard');
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Google sign-in failed');
            } finally {
              setLoading(false);
            }
          },
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          width: buttonRef.current.offsetWidth || 336,
          text,
        });
      })
      .catch(() => toast.error('Could not load Google Sign-In'));

    return () => {
      active = false;
    };
  }, [clientId, mode, nav, text]);

  if (!clientId) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E8E1D5] bg-[#FAFAFA] px-4 py-3 text-center text-sm text-[#7A7A7A]">
        Add your Google Client ID to enable Google Sign-In.
      </div>
    );
  }

  return (
    <div className="relative">
      <div ref={buttonRef} className="min-h-11 w-full grid place-items-center" />
      {loading ? (
        <div className="absolute inset-0 rounded-full bg-white/80 grid place-items-center">
          <Loader2 className="h-5 w-5 animate-spin text-[#1E3F33]" />
        </div>
      ) : null}
    </div>
  );
}
